import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Key as KeyIcon,
  Security as SecurityIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccess, showError } from '../../../Components/reuse/Toast';
import LinearLoading from '../../../Components/reuse/LinearLoading';
import useCallOnce from '../../../hooks/useCallOnce';
import { saveRedoConfig, disconnectRedo } from '../../../redux/actions/redoActions';
import { validateToken } from '../../../redux/actions/validateAction';
import redoicon from '../../../images/redoicon.png';

function RedoIntegration() {
  const dispatch = useDispatch();
  const { redoData, saving, error, validationErrors, lastMessage } = useSelector((state) => state.redo);
  const user = useSelector((state) => state.auth.user);
  
  // Local state
  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    token: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showId, setShowId] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [isRefreshingConnection, setIsRefreshingConnection] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Check Redo connection on component mount
  useCallOnce('redoIntegration/checkRedo', () => {
    dispatch(validateToken());
  });

  // Handle success messages from Redux actions
  useEffect(() => {
    if (lastMessage && !error) {
      // Close modal and reset form after successful creation
      if (connectionModalOpen) {
        setConnectionModalOpen(false);
        setFormData({ id: '', token: '' });
        setFormErrors({});
        
        // After successful creation, check connection status again
        // Only if we're not already refreshing
        if (!isRefreshingConnection) {
          setIsRefreshingConnection(true);
          setTimeout(() => {
            dispatch(validateToken());
            setIsRefreshingConnection(false);
          }, 1000); // Wait 1 second for the backend to process
        }
      }
    }
  }, [lastMessage, error, connectionModalOpen, dispatch, isRefreshingConnection]);

  // Handle API validation errors
  useEffect(() => {
    if (validationErrors && connectionModalOpen) {
      const apiFieldErrors = mapApiValidationErrors(validationErrors);
      setFormErrors(apiFieldErrors);
    }
  }, [validationErrors, connectionModalOpen]);

  // Check if Redo is connected based on user.redoConfig
  const redoConnected = user?.redoConfig && Object.keys(user.redoConfig).length > 0;

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.id.trim()) {
      errors.id = 'ID is required';
    }
    
    if (!formData.token.trim()) {
      errors.token = 'Token is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    const payload = {
      id: formData.id,
      token: formData.token
    };

    dispatch(saveRedoConfig(payload));
  };

  // Handle form field changes
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    
    // Also clear API validation errors for this field
    if (validationErrors && validationErrors.some(err => err.path === field)) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Copy to clipboard function
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied to clipboard!');
  };

  // Toggle visibility functions
  const toggleVisibility = (field) => {
    switch (field) {
      case 'id':
        setShowId(!showId);
        break;
      case 'token':
        setShowToken(!showToken);
        break;
      default:
        break;
    }
  };

  // Open connection modal
  const handleRedoSetup = () => {
    setConnectionModalOpen(true);
    setFormData({ id: '', token: '' });
    setFormErrors({});
  };

  // Close connection modal
  const handleCloseModal = () => {
    setConnectionModalOpen(false);
    setFormData({ id: '', token: '' });
    setFormErrors({});
  };

  // Map API validation errors to form field errors
  const mapApiValidationErrors = (apiErrors) => {
    if (!apiErrors || !Array.isArray(apiErrors)) return {};
    
    const fieldErrors = {};
    apiErrors.forEach(error => {
      if (error.path && error.msg) {
        fieldErrors[error.path] = error.msg;
      }
    });
    return fieldErrors;
  };


  return (
    <Box>
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          bgcolor: '#ffffff',
          border: '1px solid #e5e7eb',
          height: '100%',
          minHeight: 350,
          cursor: redoConnected ? 'default' : 'pointer',
          '&:hover': !redoConnected ? {
            boxShadow: 4,
            borderColor: '#6b7280'
          } : {},
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={!redoConnected ? handleRedoSetup : undefined}
      >
        <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ 
              width: 64, 
              height: 64, 
              borderRadius: '50%', 
              bgcolor: redoConnected ? '#f0fdf4' : '#f3f4f6',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              border: redoConnected ? '2px solid #bbf7d0' : 'none'
            }}>
              {/* <ExtensionIcon sx={{ 
                fontSize: 32, 
                color: redoConnected ? '#16a34a' : '#6b7280' 
              }} /> */}
              <img src={redoicon} alt="Shopify" style={{ width: 64, height: 64, borderRadius: '8px' }} />

            </Box>
            
            <Typography variant="h6" sx={{ 
              fontWeight: 600, 
              mb: 2, 
              color: redoConnected ? '#16a34a' : '#111827',
            }}>
              Redo
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {redoConnected ? (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  p: 2,
                  bgcolor: '#f0fdf4',
                  borderRadius: 2,
                  border: '2px solid #bbf7d0',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.15)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(34, 197, 94, 0.25)',
                    transform: 'translateY(-2px)'
                  }
                }}>
                  <CheckCircleIcon sx={{ 
                    color: '#16a34a', 
                    mr: 1, 
                    fontSize: 24,
                    filter: 'drop-shadow(0 2px 4px rgba(34, 197, 94, 0.3))'
                  }} />
                  <Typography variant="body2" sx={{ 
                    color: '#15803d', 
                    fontWeight: 600,
                    fontSize: '14px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Connected
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setDetailsModalOpen(true)}
                    sx={{
                      color: '#6b7280',
                      '&:hover': {
                        backgroundColor: '#f3f4f6',
                      }
                    }}
                  >
                    View Connection Details
                  </Button>
                </Box>
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="text" size="small" onClick={() => setConfirmOpen(true)} sx={{ color: '#ffffff', backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f' }}}>Disconnect</Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                  Elevate shopper experiences with premium returns, order management, conversion & email marketing.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Click to configure Redo integration settings
                </Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>
       
      {/* Redo Connection Modal */}
      <Dialog
        open={connectionModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          borderBottom: '1px solid #e5e7eb',
          pb: 2,
          mb: 0
        }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            borderRadius: '50%', 
            bgcolor: '#111827',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}>
            <ExtensionIcon sx={{ fontSize: 20, color: '#ffffff' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Configure Redo Integration
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container pt={4} spacing={3}>
            {/* ID */}
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Redo ID"
                type={showId ? 'text' : 'password'}
                value={formData.id}
                onChange={handleChange('id')}
                placeholder="Enter your Redo ID"
                error={!!formErrors.id}
                helperText={formErrors.id}
                size="medium"
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        onClick={() => toggleVisibility('id')}
                        edge="end"
                        size="small"
                      >
                        {showId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      {formData.id && (
                        <IconButton
                          onClick={() => handleCopyToClipboard(formData.id)}
                          edge="end"
                          size="small"
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      )}
                    </Box>
                  )
                }}
              />
            </Grid>

            {/* Token */}
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Redo Token"
                type={showToken ? 'text' : 'password'}
                value={formData.token}
                onChange={handleChange('token')}
                placeholder="Enter your Redo Token"
                error={!!formErrors.token}
                helperText={formErrors.token}
                size="medium"
                InputProps={{
                  startAdornment: <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        onClick={() => toggleVisibility('token')}
                        edge="end"
                        size="small"
                      >
                        {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      {formData.token && (
                        <IconButton
                          onClick={() => handleCopyToClipboard(formData.token)}
                          edge="end"
                          size="small"
                          sx={{ ml: 0.5 }}
                        >
                          <ContentCopyIcon />
                        </IconButton>
                      )}
                    </Box>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', mt: 0 }}>
          <Button 
            onClick={handleCloseModal} 
            color="inherit"
            disabled={saving}
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {saving ? 'Configuring...' : 'Configure Redo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect confirmation dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Disconnect Redo?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>This will remove your Redo connection from this account.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={async () => { const res = await dispatch(disconnectRedo()); if (res?.success) { setConfirmOpen(false); dispatch(validateToken()); } }} variant="contained" color="error" disabled={saving}>
            Disconnect
          </Button>
        </DialogActions>
      </Dialog>

      {/* Connection Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          borderBottom: '1px solid #e5e7eb',
          pb: 2,
          mb: 0
        }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            borderRadius: '50%', 
            bgcolor: '#16a34a',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center'
          }}>
            <CheckCircleIcon sx={{ fontSize: 20, color: '#ffffff' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Redo Connection Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container pt={4} spacing={3}>
            {/* ID */}
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Redo ID"
                type={showId ? 'text' : 'password'}
                value={user?.redoConfig?.id || ''}
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        onClick={() => toggleVisibility('id')}
                        edge="end"
                        size="small"
                      >
                        {showId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => handleCopyToClipboard(user?.redoConfig?.id || '')}
                        edge="end"
                        size="small"
                        sx={{ ml: 0.5 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  )
                }}
                sx={{ '& .MuiInputBase-input': { color: '#6b7280' } }}
              />
            </Grid>

            {/* Token */}
            <Grid size={{ xs: 12, md: 12 }}>
              <TextField
                fullWidth
                label="Redo Token"
                type={showToken ? 'text' : 'password'}
                value={user?.redoConfig?.token || ''}
                InputProps={{
                  startAdornment: <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        onClick={() => toggleVisibility('token')}
                        edge="end"
                        size="small"
                      >
                        {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton
                        onClick={() => handleCopyToClipboard(user?.redoConfig?.token || '')}
                        edge="end"
                        size="small"
                        sx={{ ml: 0.5 }}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  )
                }}
                sx={{ '& .MuiInputBase-input': { color: '#6b7280' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', mt: 0 }}>
          <Button 
            onClick={() => setDetailsModalOpen(false)} 
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RedoIntegration;
